/**
 * Global Class Schedules page — one card per recurring class schedule
 * pattern (class_id + schedule_id) across all staff. Distinct from the
 * Class Sessions page (dated occurrences). Reuses the same card + filter
 * design as the Staff Profile Class Schedules tab to keep the two pages
 * visually and behaviourally identical.
 */
import React, { useMemo, useState } from "react";
import { Box, Button, Typography, alpha } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { useLocation, useNavigate } from "react-router-dom";
import ClassSchedulesTab from "../Staff/View/Component/ClassSchedulesTab";
import useUser from "../../Core/hook/useUser";
import { Privileges } from "../../Core/Enum/enum";
import { buildCreateReturnSearch } from "../Classes/Create/assignmentContext";
import LocationLegend from "../../Components/LocationLegend";
import { useGetallLocationQuery } from "../../Api/attoDeskApi";
import SelectStudentDialog from "../ClassSchedule/List/SelectStudentDialog";
import { useCreateClassPath } from "../StaffPortal/shared/routes";

const ClassSchedulesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userHasRight } = useUser();
  const canEdit = userHasRight(Privileges.ClassScheduleEdit);
  // /class-schedules is a shared page: admins reach it via ACADEMICS →
  // Class Schedules; staff-role users reach it via the same sidebar
  // entry (Privileges.ClassScheduleView, no staffOnly gate). The Create
  // Class button must send each audience to the surface that respects
  // their scope — admin StaffPicker vs Staff Portal auth-locked page.
  const createClassPath = useCreateClassPath();
  const { data: locationData } = useGetallLocationQuery({ searchText: "" });
  const locations = Array.isArray((locationData as any)?.data)
    ? ((locationData as any).data as any[])
    : [];

  // Class-created banner. Class Schedules is the return target of the
  // Create Class round-trip (see onAddClass below), so we may arrive here
  // with `?created_class_id=X`. Surface a persistent success + a direct
  // "Continue to Assign Student" CTA — the round-trip previously ended
  // here silently, forcing the admin to hunt down the new class and open
  // the assign flow manually with no student context.
  const createdClassId = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const v = Number(p.get("created_class_id"));
    return Number.isFinite(v) && v > 0 ? v : null;
  }, [location.search]);
  const [assignForClassId, setAssignForClassId] = useState<number | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  return (
    <Box
      sx={{
        p: { xs: 1, md: 2 },
        minHeight: "100%",
        background:
          "radial-gradient(1200px 500px at 0% -10%, rgba(99,102,241,0.06), transparent 60%), radial-gradient(1000px 500px at 100% -20%, rgba(14,165,233,0.05), transparent 55%)",
      }}
    >
      {createdClassId && !bannerDismissed && (
        <Box
          sx={{
            mb: 2, p: 2, borderRadius: 2,
            bgcolor: (t) => alpha(t.palette.success.main, 0.08),
            border: (t) => `1px solid ${alpha(t.palette.success.main, 0.35)}`,
            display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
          }}
          role="status"
        >
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography sx={{ fontWeight: 800, color: "#166534", fontSize: 14 }}>
              ✅ Class #{createdClassId} created
            </Typography>
            <Typography sx={{ color: "#166534", fontSize: 12.5, mt: 0.25 }}>
              Assign a student and complete billing setup to finish enrolment.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="success"
            startIcon={<PersonAddAlt1OutlinedIcon />}
            endIcon={<ArrowForwardIcon />}
            onClick={() => setAssignForClassId(createdClassId)}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
          >
            Continue to Assign Student
          </Button>
          <Button
            variant="text"
            onClick={() => setBannerDismissed(true)}
            sx={{ textTransform: "none", color: "#166534" }}
          >
            Dismiss
          </Button>
        </Box>
      )}

      <SelectStudentDialog
        open={assignForClassId !== null}
        onClose={() => setAssignForClassId(null)}
        returnUrl="/class-schedules"
        selectedClassId={assignForClassId ?? undefined}
        source="class-schedules"
      />

      <ClassSchedulesTab
        title="Class Schedules"
        subtitle="Manage recurring class patterns, enrolled students, assigned staff and future sessions."
        showKpis
        canEditSchedule={canEdit}
        // Standalone /class-schedules page — hold the entire card behind the
        // shared full-page loader until the first data response lands, so no
        // placeholder zeros (KPIs, weekday chips, "0 patterns") ever flash.
        useFullPageLoader
        onAddClass={() =>
          navigate(`${createClassPath()}?${buildCreateReturnSearch("/class-schedules", "class-schedules")}`)
        }
        // Legend lives inside the hero of the content card — never floating
        // outside it. Locations come live from the API so a new one added
        // in Settings appears here automatically; the blue "Online" swatch
        // is added because class schedules can be online-only.
        headerAdornment={<LocationLegend locations={locations} includeOnline compact />}
      />
    </Box>
  );
};

export default ClassSchedulesPage;
